import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Volume2, VolumeX, Languages, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * TranslateListenBar — floating pill in the bottom-left of every LitLab
 * public page. Text-to-speech uses the browser's built-in speechSynthesis
 * (free, offline, all languages) so it works even when the LLM key is
 * throttled. Translation goes through /api/litlab/translate (Claude).
 *
 * The bar swaps the live DOM text of `[data-litlab-translatable]`
 * containers when a language is picked, and stores the originals so
 * "English" restores instantly with zero API calls.
 */
export default function TranslateListenBar() {
  const [langs, setLangs] = useState([]);
  const [target, setTarget] = useState("en");
  const [speaking, setSpeaking] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [open, setOpen] = useState(false);
  const originals = useRef(new Map()); // el -> originalHTML

  useEffect(() => {
    axios.get(`${API}/litlab/translate/languages`)
      .then((r) => setLangs(r.data.languages || [])).catch(() => {});
  }, []);

  // Cleanup: stop TTS on unmount / route change
  useEffect(() => {
    return () => { try { window.speechSynthesis.cancel(); } catch { /* no-op */ } };
  }, []);

  const speakOrStop = useCallback(() => {
    try {
      if (speaking) {
        window.speechSynthesis.cancel();
        setSpeaking(false);
        return;
      }
      const main = document.querySelector("main, article, .litlab-page-content") || document.body;
      const text = main.innerText.replace(/\s+/g, " ").trim().slice(0, 4000);
      if (!text) { toast.info("Nothing to read"); return; }
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.0; u.pitch = 1.0;
      // Pick a voice matching the current target language
      const voices = window.speechSynthesis.getVoices();
      const match = voices.find((v) => v.lang.toLowerCase().startsWith(target.toLowerCase()));
      if (match) u.voice = match;
      u.lang = target;
      u.onend = () => setSpeaking(false);
      u.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
      setSpeaking(true);
    } catch (e) {
      toast.error("Your browser blocked text-to-speech");
    }
  }, [speaking, target]);

  const restore = useCallback(() => {
    originals.current.forEach((html, el) => {
      try { el.innerHTML = html; } catch { /* no-op */ }
    });
    originals.current.clear();
    setTarget("en");
  }, []);

  const translateNow = useCallback(async (code) => {
    if (!code || code === "en") { restore(); return; }
    setTranslating(true);
    try {
      const els = Array.from(document.querySelectorAll(
        "main h1, main h2, main h3, main h4, main p, main li, "
        + "[data-litlab-translatable]"));
      // Filter out elements that are visibly nested inside another we're
      // already translating (avoid double-translating list items).
      const toTranslate = els.filter((el) => {
        return !els.some((other) => other !== el && other.contains(el));
      }).filter((el) => (el.innerText || "").trim().length > 3);

      for (const el of toTranslate) {
        if (!originals.current.has(el)) originals.current.set(el, el.innerHTML);
        const src = el.innerHTML;
        try {
          const r = await axios.post(`${API}/litlab/translate`,
            { text: src, target_lang: code });
          if (r.data?.translated) el.innerHTML = r.data.translated;
        } catch {
          // Skip failed nodes; keep the rest working
        }
      }
      setTarget(code);
      toast.success(`Page translated`);
    } finally {
      setTranslating(false);
    }
  }, [restore]);

  return (
    <div className="fixed left-4 bottom-4 z-40 flex items-center gap-2"
         data-testid="litlab-tts-translate-bar">
      {!open ? (
        <button onClick={() => setOpen(true)}
                className="rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 px-3 py-2 text-sm flex items-center gap-2"
                data-testid="litlab-ttbar-open">
          <Volume2 className="w-4 h-4" /> Listen · Translate
        </button>
      ) : (
        <div className="rounded-full bg-white ring-1 ring-slate-200 shadow-lg pl-2 pr-1 py-1 flex items-center gap-1 text-sm"
             data-testid="litlab-ttbar-panel">
          <button onClick={speakOrStop}
                  className={`rounded-full px-3 py-1.5 flex items-center gap-1 ${
                    speaking ? "bg-rose-600 text-white" : "bg-sky-700 text-white"}`}
                  data-testid="litlab-ttbar-listen">
            {speaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            {speaking ? "Stop" : "Listen"}
          </button>
          <div className="flex items-center gap-1 pl-1">
            {translating
              ? <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
              : <Languages className="w-4 h-4 text-slate-500" />}
            <select value={target} onChange={(e) => translateNow(e.target.value)}
                    disabled={translating}
                    className="rounded-md text-sm border border-slate-200 px-2 py-1"
                    data-testid="litlab-ttbar-lang">
              <option value="en">English</option>
              {langs.map((l) => (
                <option key={l.code} value={l.code}>{l.name}</option>
              ))}
            </select>
          </div>
          <button onClick={() => { window.speechSynthesis?.cancel(); setOpen(false); restore(); }}
                  className="p-1 text-slate-400 hover:text-slate-800"
                  data-testid="litlab-ttbar-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
